/**
 * HotelsDesktopPresenter — Desktop UI strategy for hotels.
 *
 * Renders rooms via HotelsPicker inside the shadow-root #ttpHotelsArea,
 * manages panel expansion/shrinking, compact voice mode switching,
 * FlyToCart animation, booking toast, and BookingSummary.
 */
import { HotelsPicker } from './HotelsPicker.js';
import { BookingSummary } from './BookingSummary.js';
import { FlyToCart } from '../ecommerce/FlyToCart.js';
import { generateHotelsCSS } from './hotelsStyles.js';

export class HotelsDesktopPresenter {

  constructor(config = {}) {
    this.shadowRoot = config.shadowRoot;
    this.primaryColor = config.primaryColor || '#2563eb';
    this.onRoomBook = config.onRoomBook || (() => {});

    this.picker = new HotelsPicker({
      primaryColor: this.primaryColor,
      onRoomBook: (cardEl, room) => this._handleRoomBook(cardEl, room),
      onClose: () => this._handlePickerClose(),
    });

    this.bookingSummary = new BookingSummary();

    this.flyToCart = new FlyToCart({
      getCartTarget: () => this._getFlyTarget(),
      onCartBump: () => {},
      onRipple: () => {},
    });

    this.container = document.createElement('div');
    this.container.id = 'ttpHotelsArea';
    this.container.className = 'ttp-hotels-area';

    this._toastEl = null;
    this._toastTimer = null;
    this.voiceActiveState = null;
    this.isAttached = false;
    this._activatedByPresenter = false;
  }

  _getFlyTarget() {
    if (this.bookingSummary.element) {
      const r = this.bookingSummary.element.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return this.bookingSummary.element;
    }
    return this.container;
  }

  _handleRoomBook(cardEl, room) {
    if (this.flyToCart.isAnimating) return;

    this.picker.element?.classList.add('ttp-products-animating');
    this.flyToCart.triggerFly(cardEl, room, () => {
      this.picker.element?.classList.remove('ttp-products-animating');
      this.onRoomBook(room);
      this._showBookedToast(room);
    }, { preferClone: false });
  }

  _showBookedToast(room) {
    this._dismissToast(true);

    this.picker.hide();
    this._shrinkPanel();
    this._restoreFullVoiceMode();
    this._setContainerMode('summary');

    const toast = document.createElement('div');
    toast.className = 'ttp-cart-toast';
    toast.innerHTML = `
      <div class="ttp-toast-icon">\u2713</div>
      <div class="ttp-toast-content">
        <div class="ttp-toast-title">Room booked!</div>
        <div class="ttp-toast-product">${room.name}</div>
      </div>
    `;

    const controls = this.shadowRoot?.querySelector('.ttp-controls');
    if (controls) {
      controls.parentNode.insertBefore(toast, controls);
    } else if (this.voiceActiveState) {
      this.voiceActiveState.appendChild(toast);
    }

    this._toastEl = toast;
    this._toastTimer = setTimeout(() => this._dismissToast(), 3000);
  }

  _dismissToast(skipRestore = false) {
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
    if (this._toastEl?.parentNode) {
      this._toastEl.classList.add('ttp-toast-hiding');
      const el = this._toastEl;
      this._toastEl = null;
      setTimeout(() => {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 300);
    }
  }

  showRooms(rooms, title) {
    this._ensureReady();
    this._ensureActiveStateVisible();
    this._switchToCompactMode();
    this._setContainerMode('rooms');

    this.picker.hide();

    const pickerEl = this.picker.show(rooms, title);
    if (pickerEl) {
      this.container.insertBefore(pickerEl, this.container.firstChild);
    }
    this._expandPanelToFit(rooms.length);
  }

  hideRooms() {
    this.picker.hide();
    this._shrinkPanel();
    this._restoreFullVoiceMode();
    this._setContainerMode('summary');
    this._restoreIdleStateIfNeeded();
  }

  updateBooking(bookingInfo) {
    this._ensureReady();
    if (bookingInfo) {
      this.bookingSummary.update(bookingInfo);
    }
  }

  destroy() {
    if (this.flyToCart) this.flyToCart.destroy();
    this._dismissToast(true);
    this.picker.hide();
    this._shrinkPanel();
    this._restoreFullVoiceMode();
    if (this.voiceActiveState) {
      this.voiceActiveState.classList.remove('ttp-rooms-visible');
    }
    if (this.bookingSummary.element?.parentNode) {
      this.bookingSummary.element.parentNode.removeChild(this.bookingSummary.element);
    }
    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.isAttached = false;
  }

  _ensureReady() {
    if (!this.isAttached) {
      this._injectStyles();
      this._attachToDOM();
    }
  }

  _injectStyles() {
    if (!this.shadowRoot) return;
    if (this.shadowRoot.querySelector('[data-ttp-hotels]')) return;

    const css = generateHotelsCSS();
    const style = document.createElement('style');
    style.setAttribute('data-ttp-hotels', '');
    style.textContent = css;
    this.shadowRoot.appendChild(style);
  }

  _attachToDOM() {
    if (this.isAttached || !this.shadowRoot) return;

    this.voiceActiveState = this.shadowRoot.getElementById('voiceActiveState');
    if (!this.voiceActiveState) {
      console.warn('[HotelsDesktopPresenter] #voiceActiveState not found');
      return;
    }

    if (this.shadowRoot.getElementById('ttpHotelsArea')) {
      this.isAttached = true;
      return;
    }

    const compactSection = this.voiceActiveState.querySelector('.compact-voice-section');
    const desktopSection = this.voiceActiveState.querySelector('.desktop-voice-section');
    const insertAfter = compactSection || desktopSection;

    if (insertAfter?.nextSibling) {
      this.voiceActiveState.insertBefore(this.container, insertAfter.nextSibling);
    } else {
      this.voiceActiveState.appendChild(this.container);
    }

    const inputArea = this.voiceActiveState.querySelector('.voice-text-input-area');
    if (inputArea) {
      const summaryEl = this.bookingSummary.render();
      summaryEl.classList.add('ttp-booking-summary-inline');
      inputArea.appendChild(summaryEl);
    }

    this.isAttached = true;
  }

  _setContainerMode(mode) {
    if (mode === 'rooms') {
      this.container.style.flex = '1 1 0';
      this.container.style.minHeight = '0';
      this.container.style.overflowY = 'auto';
      if (this.voiceActiveState) this.voiceActiveState.classList.add('ttp-rooms-visible');
    } else {
      this.container.style.flex = '0 0 auto';
      this.container.style.minHeight = '';
      this.container.style.overflowY = '';
      if (this.voiceActiveState) this.voiceActiveState.classList.remove('ttp-rooms-visible');
    }
  }

  _expandPanelToFit(_roomCount) {
    if (!this.shadowRoot) return;
    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (!panel) return;

    panel.classList.add('ttp-panel-expanded');

    requestAnimationFrame(() => {
      const compact = this.voiceActiveState?.querySelector('.compact-voice-section');
      const picker = this.container?.querySelector('.ttp-product-picker');
      const transcript = this.voiceActiveState?.querySelector('.desktop-conversation-panel');
      const inputRow = this.voiceActiveState?.querySelector('.voice-text-input-area');

      let needed = 0;
      if (compact) needed += compact.offsetHeight;
      if (picker) {
        // scrollHeight gives the full content height even when the
        // flex layout has constrained the picker's visible area.
        // Add border (2px) and vertical margin (8px top + 8px bottom).
        needed += picker.scrollHeight + 18;
      }
      if (transcript) needed += Math.min(transcript.offsetHeight, 100);
      if (inputRow) needed += inputRow.offsetHeight;
      needed += 60;

      const maxHeight = Math.min(800, Math.floor(window.innerHeight * 0.9));
      const height = Math.min(Math.max(needed, 380), maxHeight);
      panel.style.height = `${height}px`;
    });
  }

  _shrinkPanel() {
    if (!this.shadowRoot) return;
    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (!panel) return;
    panel.classList.remove('ttp-panel-expanded');
    panel.style.height = '';
  }

  _switchToCompactMode() {
    if (!this.shadowRoot) return;
    const originalSection = this.shadowRoot.getElementById('desktopVoiceSection');
    const compactSection = this.shadowRoot.getElementById('compactVoiceSection');
    if (originalSection) originalSection.style.display = 'none';
    if (compactSection) compactSection.style.display = 'flex';
  }

  _restoreFullVoiceMode() {
    if (!this.shadowRoot) return;
    const originalSection = this.shadowRoot.getElementById('desktopVoiceSection');
    const compactSection = this.shadowRoot.getElementById('compactVoiceSection');
    if (originalSection && compactSection) {
      originalSection.style.display = '';
      compactSection.style.display = 'none';
    }
  }

  _ensureActiveStateVisible() {
    if (!this.shadowRoot) return;

    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (panel && !panel.classList.contains('open')) {
      panel.classList.add('open');
    }

    const landing = this.shadowRoot.getElementById('landingScreen');
    if (landing) landing.classList.remove('active');

    const textIf = this.shadowRoot.getElementById('textInterface');
    if (textIf) textIf.classList.remove('active');

    const vi = this.shadowRoot.getElementById('voiceInterface');
    if (vi && !vi.classList.contains('active')) {
      vi.classList.add('active');
    }

    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    if (!activeState) return;

    const isHidden = activeState.style.display === 'none' ||
      getComputedStyle(activeState).display === 'none';
    if (isHidden) {
      const idleState = this.shadowRoot.getElementById('voiceIdleState');
      if (idleState) idleState.style.display = 'none';
      activeState.style.display = 'flex';
      this._activatedByPresenter = true;
    }
  }

  _restoreIdleStateIfNeeded() {
    if (!this._activatedByPresenter || !this.shadowRoot) return;
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    if (activeState) activeState.style.display = 'none';
    if (idleState) idleState.style.display = 'flex';
    this._activatedByPresenter = false;
  }

  _handlePickerClose() {
    this.hideRooms();
  }
}
